// color-util (Task 8) Refs: R2
// Provides sentiment triple formatting and CSS class mapping based on dominant sentiment.

export interface Sentiments { positive: number; neutral: number; negative: number; }
export type Dominant = 'positive' | 'neutral' | 'negative' | 'empty' | 'mixed';

// Format triple positive:neutral:negative (R2)
export function formatTriple(s: Sentiments): string {
  return `${s.positive}:${s.neutral}:${s.negative}`; // R2
}

function dominantSentiment(s: Sentiments): Dominant {
  const { positive, neutral, negative } = s;
  if (positive === 0 && neutral === 0 && negative === 0) return 'empty';
  // Determine strict max
  const entries: Array<[Dominant, number]> = [
    ['positive', positive],
    ['neutral', neutral],
    ['negative', negative],
  ];
  entries.sort((a,b) => b[1] - a[1]);
  const [first, second] = entries;
  if (first[1] === second[1]) return 'mixed';
  return first[0];
}

// Map to CSS class (R2)
export function sentimentClass(s: Sentiments): string {
  const dom = dominantSentiment(s);
  switch(dom) {
    case 'empty': return 'sentiment-empty';
    case 'positive': return 'sentiment-pos';
    case 'neutral': return 'sentiment-neu';
    case 'negative': return 'sentiment-neg';
    case 'mixed':
    default: return 'sentiment-mixed';
  }
}

export const SENTIMENT_GRADIENT_COLORS = {
  negative: '#e24b4b',
  neutral: '#b4b8be',
  positive: '#3db86a',
} as const;

function clampCount(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function formatPct(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** Max each side of a boundary may fade, in percent of the card height. */
const MAX_BLEND_HALF_PCT = 4;

function blendHalfPct(leftShare: number, rightShare: number): number {
  return Math.min(MAX_BLEND_HALF_PCT, Math.min(leftShare, rightShare) * 0.4);
}

/**
 * Top-to-bottom bands of negative (red), neutral (gray), and positive (green).
 * Each band is sized by that day's count share so a 78% neutral day stays
 * mostly gray; a short fade at each boundary avoids a hard stripe. (R2)
 */
export function sentimentGradient(s: Sentiments): string {
  const negative = clampCount(s.negative);
  const neutral = clampCount(s.neutral);
  const positive = clampCount(s.positive);
  const total = negative + neutral + positive;
  if (total === 0) return '';

  const bands = [
    { color: SENTIMENT_GRADIENT_COLORS.negative, count: negative },
    { color: SENTIMENT_GRADIENT_COLORS.neutral, count: neutral },
    { color: SENTIMENT_GRADIENT_COLORS.positive, count: positive },
  ].filter((band) => band.count > 0);

  if (bands.length === 1) {
    const color = bands[0].color;
    return `linear-gradient(to bottom, ${color} 0%, ${color} 100%)`;
  }

  const shares = bands.map((band) => (band.count / total) * 100);
  const stops: string[] = [`${bands[0].color} 0%`];
  let cursor = 0;
  for (let i = 0; i < bands.length - 1; i += 1) {
    const boundary = cursor + shares[i];
    const half = blendHalfPct(shares[i], shares[i + 1]);
    stops.push(`${bands[i].color} ${formatPct(boundary - half)}%`);
    stops.push(`${bands[i + 1].color} ${formatPct(boundary + half)}%`);
    cursor = boundary;
  }
  stops.push(`${bands[bands.length - 1].color} 100%`);
  return `linear-gradient(to bottom, ${stops.join(', ')})`;
}
